// Shared plan data for /get-started2 (marketing cards) and /checkout-plan/:tier
// (the full checkout page). Keeping this in one place means both pages always
// agree on Chargebee plan ids, pricing, and copy.

export const NETWORK_PLAN = { monthly: 'Network-Member---Monthly', annual: 'Network-Member-Annual' };
export const VERIFIED_PLAN = { monthly: 'Verified-Coach-Monthly', annual: 'Verified-Coach---Annual' };
export const LIFETIME_PLAN = 'First-Mover-Lifetime-Forever';

// Accepts the same ?plan= / ?intent= link conventions already used on the
// marketing site (join-network.html, intendedPlan.js) — a tier alias
// (network/verified/lifetime), optionally combined with a period
// (network-monthly, verified-annual), or the full Chargebee plan id.
// Returns { tier, period } or null if nothing recognizable is present.
export function parseLinkedPlan(search) {
  const params = new URLSearchParams(search || '');
  const raw = params.get('plan') || params.get('intent');
  if (!raw) return null;
  const s = raw.toLowerCase();
  let tier = null;
  if (s.indexOf('lifetime') !== -1) tier = 'lifetime';
  else if (s.indexOf('verified') !== -1) tier = 'verified';
  else if (s.indexOf('network') !== -1) tier = 'network';
  if (!tier) return null;

  const periodParam = (params.get('period') || '').toLowerCase();
  const period = periodParam === 'annual' || s.indexOf('annual') !== -1 ? 'annual' : 'monthly';
  return { tier, period };
}

// Feature bullets shared by the Verified Coach and Lifetime tiers (Lifetime
// gets these plus two lifetime-specific bullets appended below).
const CORE_FEATURES = [
  {
    title: 'Professional marketplace profile',
    detail:
      'a credentialed presence that positions you the way your work deserves, visible to the Viveka community',
  },
  {
    title: 'Resource library and community',
    detail:
      'access ongoing resources and connect with other coaches building real, professional practices',
  },
  {
    title: 'Scheduling and booking',
    detail: 'manage your calendar and client bookings without juggling separate tools',
  },
  {
    title: 'Accountability tracker',
    detail: 'keep clients on track between sessions with built-in progress tools',
  },
  {
    title: 'Program management',
    detail: 'build and run structured coaching programs directly on the platform',
  },
  {
    title: 'Marketplace Ads',
    detail: 'promote your programs and services to coaches and clients browsing the network',
  },
];

const VERIFIED_ONLY_FEATURES = [
  {
    title: 'Viveka Verification badge',
    detail: 'signals to organizations and clients that your credentials have been confirmed',
  },
  {
    title: 'Priority search placement',
    detail:
      'your profile is prioritized ahead of standard listings, so the right people see you first',
  },
  {
    title: 'File uploads',
    detail: 'share client programs, materials, and resources directly through your profile',
  },
  {
    title: 'Reporting and analytics',
    detail: "see exactly who's viewing your profile and how your presence is performing",
  },
  {
    title: 'Enterprise client opportunities',
    detail:
      'become eligible for matching with organizations seeking credentialed coaches for their teams',
  },
  {
    title: 'Viveka spotlight features',
    detail: "get featured in Viveka's spotlight opportunities and case studies",
  },
];

// Copy + pricing shared by the marketing cards and the checkout page's left
// column. `description` (and `descriptionKicker`) are only shown on the
// checkout page (the marketing cards only have room for the bullet list).
export const PLAN_CATALOG = {
  network: {
    badge: 'Network Member',
    title: 'Viveka Network Member',
    description: 'This is your foundation — a real professional home, built for coaches who are ready to be seen.',
    features: [
      ...CORE_FEATURES,
      {
        title: '3 active listings',
        detail: 'showcase up to 3 programs or services at a time in the marketplace',
      },
      {
        title: 'Monthly coaching industry insights',
        detail: 'stay current with trends and data relevant to your practice',
      },
    ],
    price: { monthly: '$19', annual: '$197' },
    savingsNote: 'Save $31/year vs monthly',
  },
  verified: {
    badge: 'Verified Coach',
    title: 'Viveka Verified Coach',
    description:
      'This is the complete Viveka experience: full visibility, full tools, and a direct line to the organizations actively looking for coaches like you.',
    features: [
      ...CORE_FEATURES,
      {
        title: '10 active listings',
        detail: 'showcase up to 10 programs or services at a time in the marketplace',
      },
      {
        title: 'Monthly coaching industry insights',
        detail: 'stay current with trends and data relevant to your practice',
      },
      ...VERIFIED_ONLY_FEATURES,
    ],
    price: { monthly: '$49', annual: '$497' },
    savingsNote: 'Save $91/year vs monthly',
  },
  lifetime: {
    badge: 'Viveka First 100',
    title: 'Verified Coach — Lifetime',
    descriptionKicker: "You're Claiming a Founding Seat:",
    description:
      "This is one payment of $997, not a subscription, not an introductory rate. Once you're in, you're in for life. No monthly fee. No renewal. No price changes, no matter how the platform grows.",
    features: [
      ...CORE_FEATURES,
      {
        title: '10 active listings',
        detail: 'showcase up to 10 programs or services at a time in the marketplace',
      },
      {
        title: 'Monthly coaching industry insights',
        detail: 'stay current with trends and data relevant to your practice',
      },
      ...VERIFIED_ONLY_FEATURES,
      {
        title: 'Lifetime status, no renewals',
        detail: 'your access never expires and never requires another payment',
      },
      {
        title: 'Locked-in price, no future increases',
        detail:
          "whatever Verified Coach costs down the road, you'll never pay it. Your rate is frozen the day you join",
      },
    ],
    price: { oneTime: '$997' },
  },
};

// Resolves a tier + period into the Chargebee plan id it should check out
// with. Returns null for an unrecognized tier.
export function resolvePlanId(tier, period) {
  if (tier === 'lifetime') return LIFETIME_PLAN;
  if (tier === 'network') return NETWORK_PLAN[period === 'annual' ? 'annual' : 'monthly'];
  if (tier === 'verified') return VERIFIED_PLAN[period === 'annual' ? 'annual' : 'monthly'];
  return null;
}
